// src/pages/partners/partners-logged-in/routes/exercise-vault/MealPlansVaultList.tsx
import * as React from "react";
import {
  Box,
  Stack,
  Typography,
  Chip,
  Drawer,
  IconButton,
  Button,
  TextField,
  Autocomplete,
  Checkbox,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";

import GlassContainer from "../../../components/liquid-glass";
import VaultGridCard from "./VaultGridCard";

import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../../../../redux/store";

import {
  MealPlans as MealPlanType,
  Recipe,
  SchedulePlan,
} from "../../../../../models/types";

import {
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import { poolData } from "../groups";

import {
  upsertTrainerMealPlans,
  deleteTrainerMealPlan,
} from "../../../../../redux/thunks/thunk-partners";

export interface MealPlansVaultListProps {
  mealPlans: MealPlanType[];
  recipes: Recipe[];

  // optimistic local updates pushed up to parent (ExerciseVault.tsx)
  onCreateOrUpdateMealPlan: (plan: MealPlanType) => void;
  onDeleteMealPlan: (planId: string) => void;
}

type MealPlanDraft = {
  id: string;
  title: string;
  tags: string[];
  recipes: { userId: string; recipesId: string }[];
  plannedUse?: SchedulePlan;
};

type SortKey = "title";

const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

// Generate pastel colors for tags based on the tag string
function getTagColors(tag: string): {
  backgroundColor: string;
  color: string;
  borderColor: string;
} {
  let hash = 0;
  for (let i = 0; i < tag.length; i++) {
    hash = (hash * 31 + tag.charCodeAt(i)) | 0;
  }
  const hue = Math.abs(hash) % 360;
  // pastel-ish background, darker text/border
  const backgroundColor = `hsl(${hue}, 70%, 88%)`;
  const borderColor = `hsl(${hue}, 60%, 60%)`;
  const color = `hsl(${hue}, 70%, 25%)`;
  return { backgroundColor, color, borderColor };
}

// weekday helpers (for future assign/scheduling UI)
const weekdayOrder: (keyof SchedulePlan["weekdays"])[] = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];
const weekdayShort: Record<keyof SchedulePlan["weekdays"], string> = {
  sunday: "Sun",
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
};
const hasAnyWeekday = (p?: SchedulePlan | null) =>
  !!p &&
  !!(
    p.weekdays.sunday ||
    p.weekdays.monday ||
    p.weekdays.tuesday ||
    p.weekdays.wednesday ||
    p.weekdays.thursday ||
    p.weekdays.friday ||
    p.weekdays.saturday
  );

const formatPlannedUse = (p?: SchedulePlan | null) => {
  if (!p || !p.startDate) return "Planned: N/A";
  const recurring = hasAnyWeekday(p);
  const range =
    p.endDate && p.endDate !== p.startDate
      ? `${p.startDate} → ${p.endDate}`
      : p.startDate;
  if (!recurring) return range;
  const days = weekdayOrder
    .filter((k) => p.weekdays[k])
    .map((k) => weekdayShort[k]);
  return `${range} • ${days.join(", ") || "—"}`;
};

// shallow-ish equality for our draft (title + tags + recipes + plannedUse basics)
function isEqualDraft(a: MealPlanDraft, b: MealPlanDraft) {
  if (a.id !== b.id) return false;
  if ((a.title || "").trim() !== (b.title || "").trim()) return false;

  // compare tags arrays (order-insensitive)
  const aTags = [...(a.tags || [])].sort();
  const bTags = [...(b.tags || [])].sort();
  if (aTags.length !== bTags.length) return false;
  for (let i = 0; i < aTags.length; i++) {
    if (aTags[i] !== bTags[i]) return false;
  }

  // compare recipes arrays (order-sensitive; matches our UI behavior)
  if (a.recipes.length !== b.recipes.length) return false;
  for (let i = 0; i < a.recipes.length; i++) {
    const ar = a.recipes[i];
    const br = b.recipes[i];
    if (!br) return false;
    if (ar.userId !== br.userId || ar.recipesId !== br.recipesId) return false;
  }

  // plannedUse basic fields if present (we don't edit this yet in UI, but keep guard)
  const ap = a.plannedUse;
  const bp = b.plannedUse;
  if (!ap && !bp) return true;
  if (!!ap !== !!bp) return false;
  if (ap && bp) {
    if (ap.startDate !== bp.startDate) return false;
    if (ap.endDate !== bp.endDate) return false;
    const aw = ap.weekdays || ({} as SchedulePlan["weekdays"]);
    const bw = bp.weekdays || ({} as SchedulePlan["weekdays"]);
    const keys = Object.keys(aw) as (keyof SchedulePlan["weekdays"])[];
    for (const k of keys) {
      if (aw[k] !== bw[k]) return false;
    }
  }

  return true;
}

const normalize = (s?: string | null) =>
  (s ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");

export default function MealPlansVaultList({
  mealPlans,
  recipes,
  onCreateOrUpdateMealPlan,
  onDeleteMealPlan,
}: MealPlansVaultListProps) {
  const dispatch = useDispatch<AppDispatch>();

  const darkMode = !!useSelector((state: RootState) => state.partners.darkMode);

  // consistent readable colors for entire vault
  const baseTextColor = darkMode ? "#f9fafb" : "#111827";
  const subtleTextColor = darkMode
    ? "rgba(249,250,251,0.7)"
    : "rgba(17,24,39,0.7)";

  // drawer-specific surfaces
  const drawerBg = darkMode ? "#0f0f0f" : "#fff";
  const drawerBorder = darkMode
    ? "1px solid rgba(255,255,255,0.07)"
    : "1px solid rgba(0,0,0,0.06)";
  const drawerTextColor = baseTextColor;
  const drawerSubTextColor = subtleTextColor;
  const drawerFaintBg = darkMode
    ? "rgba(255,255,255,0.02)"
    : "rgba(0,0,0,0.02)";

  const trainerUserId = useSelector(
    (s: RootState) =>
      s.partners.partnerData?.partnerData?.userId as string | undefined
  );

  const [isFetchingToken, setIsFetchingToken] = React.useState<boolean>(true);
  const [idToken, setIdToken] = React.useState<string>("");
  const [isSaving, setIsSaving] = React.useState<boolean>(false);

  React.useEffect(() => {
    setIsFetchingToken(true);
    const currentUser = new CognitoUserPool(poolData).getCurrentUser();
    if (currentUser) {
      currentUser.getSession((err: any, session: CognitoUserSession | null) => {
        if (err) {
          console.error("Error retrieving session:", err);
          setIsFetchingToken(false);
          return;
        }
        if (session && session.isValid()) {
          setIdToken(session.getIdToken().getJwtToken());
        }
        setIsFetchingToken(false);
      });
    } else {
      setIsFetchingToken(false);
    }
  }, []);

  // view mode + sorting + search + tag filters
  const [viewMode, setViewMode] = React.useState<"grid" | "list">("grid");
  const [sortBy, setSortBy] = React.useState<SortKey>("title");
  const [sortDirection, setSortDirection] = React.useState<"asc" | "desc">(
    "asc"
  );
  const [q, setQ] = React.useState("");
  const [selectedTags, setSelectedTags] = React.useState<string[]>([]);

  // all tags available across all meal plans
  const allTags = React.useMemo(
    () =>
      Array.from(
        new Set(
          mealPlans.flatMap((p) => (p.tags && p.tags.length ? p.tags : []))
        )
      ).sort((a, b) => a.localeCompare(b)),
    [mealPlans]
  );

  // drawer open/close + mode
  const [panelOpen, setPanelOpen] = React.useState(false);
  const [mode, setMode] = React.useState<"CREATE" | "EDIT">("CREATE");

  // local draft the drawer edits
  const [draft, setDraft] = React.useState<MealPlanDraft>(() => ({
    id: uid(),
    title: "New Meal Plan",
    tags: [],
    recipes: [],
    plannedUse: undefined,
  }));

  // snapshot of the draft as it was when the drawer opened (for dirty-check)
  const [openSnapshot, setOpenSnapshot] = React.useState<MealPlanDraft>(() => ({
    id: "",
    title: "",
    tags: [],
    recipes: [],
    plannedUse: undefined,
  }));

  // confirmation dialog for discard on close
  const [confirmCloseOpen, setConfirmCloseOpen] = React.useState(false);
  const isDirty = React.useMemo(
    () => !isEqualDraft(draft, openSnapshot),
    [draft, openSnapshot]
  );

  // build recipe options for multiselect
  const recipeOptions = React.useMemo(
    () =>
      recipes.map((r) => ({
        label: r.title || "Untitled Recipe",
        recipeId: r.recipeId,
        userId: r.userId,
      })),
    [recipes]
  );

  // take our stored refs [{userId,recipesId}] -> actual option objects for Autocomplete value
  const selectedRecipeOptions = React.useMemo(
    () =>
      draft.recipes
        .map((ref) => {
          const found = recipes.find((r) => r.recipeId === ref.recipesId);
          if (!found) return null;
          return {
            label: found.title || "Untitled Recipe",
            recipeId: found.recipeId,
            userId: found.userId,
          };
        })
        .filter(Boolean) as {
          label: string;
          recipeId: string;
          userId: string;
        }[],
    [draft.recipes, recipes]
  );

  // helper: summarize plan for cards/list
  const summarizeMealPlan = React.useCallback(
    (plan: MealPlanType) => {
      const refs = plan.recipes || [];
      const recipeTitles = refs
        .map((ref) => recipes.find((r) => r.recipeId === ref.recipesId)?.title)
        .filter(Boolean) as string[];
      return {
        countRecipes: refs.length,
        topTitles: recipeTitles.slice(0, 2),
      };
    },
    [recipes]
  );

  // filter by search + tags
  const filteredPlans = React.useMemo(() => {
    const t = normalize(q);
    return mealPlans.filter((plan) => {
      // tag filter
      const planTags = plan.tags || [];
      if (
        selectedTags.length > 0 &&
        !planTags.some((tag) => selectedTags.includes(tag))
      ) {
        return false;
      }

      if (!t) return true;

      const summary = summarizeMealPlan(plan);
      const hay = normalize(
        [
          plan.title,
          ...(plan.tags || []),
          ...summary.topTitles,
          summary.countRecipes ? `${summary.countRecipes} recipes` : "",
        ]
          .filter(Boolean)
          .join(" ")
      );
      return hay.includes(t);
    });
  }, [mealPlans, q, summarizeMealPlan, selectedTags]);

  // sort for list view
  const sortedForList = React.useMemo(() => {
    const list = [...filteredPlans];
    return list.sort((a, b) => {
      let aVal = "";
      let bVal = "";

      switch (sortBy) {
        case "title":
        default:
          aVal = (a.title ?? "").toLowerCase();
          bVal = (b.title ?? "").toLowerCase();
          break;
      }

      const cmp = aVal.localeCompare(bVal);
      return sortDirection === "asc" ? cmp : -cmp;
    });
  }, [filteredPlans, sortBy, sortDirection]);

  const handleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDirection("asc");
    }
  };

  // ─────────────────────────────
  // open drawer for CREATE or EDIT
  // ─────────────────────────────
  const openWithMealPlan = (plan?: MealPlanType) => {
    if (plan) {
      // EDIT existing
      const nextDraft: MealPlanDraft = {
        id: plan.id,
        title: plan.title ?? "Untitled Plan",
        tags: plan.tags ?? [],
        recipes: plan.recipes ?? [],
        plannedUse: (plan as any).plannedUse,
      };
      setDraft(nextDraft);
      setOpenSnapshot(nextDraft);
      setMode("EDIT");
    } else {
      // CREATE new
      const nextDraft: MealPlanDraft = {
        id: uid(),
        title: "New Meal Plan",
        tags: [],
        recipes: [],
        plannedUse: undefined,
      };
      setDraft(nextDraft);
      setOpenSnapshot(nextDraft);
      setMode("CREATE");
    }

    setPanelOpen(true);
  };

  const actuallyCloseDrawer = () => {
    setPanelOpen(false);
    setConfirmCloseOpen(false);
  };

  const attemptCloseDrawer = () => {
    if (isDirty) {
      setConfirmCloseOpen(true);
    } else {
      actuallyCloseDrawer();
    }
  };

  const handleCloseDrawer = () => {
    attemptCloseDrawer();
  };

  // multiselect change handler for recipes
  const handleRecipeListChange = (
    _event: any,
    values: { label: string; recipeId: string; userId: string }[]
  ) => {
    setDraft((d) => ({
      ...d,
      recipes: values.map((opt) => ({
        userId: opt.userId,
        recipesId: opt.recipeId,
      })),
    }));
  };

  const toggleTagFilter = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  // ─────────────────────────────
  // SAVE (create/update)
  // ─────────────────────────────
  const handleSave = async () => {
    if (!draft.title?.trim()) return;
    setIsSaving(true);
    try {
      const finalPlan: MealPlanType = {
        id: draft.id,
        title: draft.title,
        tags: draft.tags,
        recipes: draft.recipes,
        ...(draft.plannedUse ? { plannedUse: draft.plannedUse } : {}),
      } as MealPlanType;

      // optimistic local insert/replace in ExerciseVault state
      onCreateOrUpdateMealPlan(finalPlan);

      // server sync -> thunk-partners
      if (idToken && trainerUserId) {
        try {
          await dispatch(
            upsertTrainerMealPlans({
              idToken,
              trainerUserId,
              mealPlan: finalPlan,
            })
          ).unwrap();
        } catch (err) {
          console.error("Failed to upsert meal plan remotely:", err);
        }
      }

      // On successful save, refresh snapshot and close
      setOpenSnapshot({
        id: draft.id,
        title: draft.title,
        tags: draft.tags,
        recipes: draft.recipes,
        plannedUse: draft.plannedUse,
      });
      setPanelOpen(false);
      setConfirmCloseOpen(false);
    } finally {
      setIsSaving(false);
    }
  };

  // ─────────────────────────────
  // DELETE
  // ─────────────────────────────
  const quickDeletePlan = async (planId: string) => {
    // optimistic local removal
    onDeleteMealPlan(planId);

    // remote delete
    if (idToken && trainerUserId) {
      try {
        await dispatch(
          deleteTrainerMealPlan({
            idToken,
            trainerUserId,
            planId,
          })
        ).unwrap();
      } catch (err) {
        console.error("Failed to delete meal plan remotely:", err);
      }
    }
  };

  const handleDelete = async () => {
    if (mode !== "EDIT") {
      setPanelOpen(false);
      return;
    }

    const planId = draft.id;
    await quickDeletePlan(planId);
    setPanelOpen(false);
  };

  const handleRowDeleteClick = (
    ev: React.MouseEvent<HTMLButtonElement>,
    planId: string
  ) => {
    ev.stopPropagation();
    quickDeletePlan(planId);
  };

  // ─────────────────────────────
  // RENDER
  // ─────────────────────────────
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* ── Unified toolbar ── */}
      <Stack
        direction="row"
        spacing={1.5}
        mb={2}
        alignItems="center"
        flexWrap="wrap"
        sx={{ rowGap: 1.5 }}
      >
        {/* Search */}
        <TextField
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{
            flex: 1,
            minWidth: 200,
            maxWidth: { xs: "100%", sm: "38%" },
            "& .MuiInputBase-input": { color: baseTextColor },
          }}
          placeholder="Search meal plans…"
          size="small"
          label="Search"
          InputLabelProps={{
            sx: {
              color: baseTextColor,
              "&.Mui-focused": { color: baseTextColor },
            },
          }}
        />

        {/* Tags dropdown (multi-select) */}
        {allTags.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel
              sx={{
                color: baseTextColor,
                "&.Mui-focused": { color: baseTextColor },
              }}
            >
              Tags
            </InputLabel>
            <Select
              multiple
              value={selectedTags}
              label="Tags"
              onChange={(e) => {
                const val = e.target.value;
                setSelectedTags(typeof val === "string" ? val.split(",") : val);
              }}
              renderValue={(sel) =>
                (sel as string[]).length === 0
                  ? "All"
                  : (sel as string[]).join(", ")
              }
              sx={{
                "& .MuiInputBase-input": { color: baseTextColor },
                "& .MuiSvgIcon-root": { color: subtleTextColor },
              }}
            >
              {allTags.map((tag) => (
                <MenuItem key={tag} value={tag} sx={{ textTransform: "capitalize" }}>
                  {tag}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Spacer */}
        <Box sx={{ flex: "1 0 0", display: { xs: "none", md: "block" } }} />

        {/* View toggles */}
        <Stack direction="row" spacing={0.5} alignItems="center">
          {viewMode === "list" ? (
            <GlassContainer blur={18} radius={999} sx={{ p: 0.25 }} dontHoverPop>
              <IconButton size="small" onClick={() => setViewMode("list")} color="primary">
                <ViewListIcon fontSize="small" />
              </IconButton>
            </GlassContainer>
          ) : (
            <IconButton
              size="small"
              onClick={() => setViewMode("list")}
              color="default"
              sx={{ color: darkMode ? "rgba(249,250,251,0.65)" : "#111827" }}
            >
              <ViewListIcon fontSize="small" />
            </IconButton>
          )}

          {viewMode === "grid" ? (
            <GlassContainer blur={18} radius={999} sx={{ p: 0.25 }} dontHoverPop>
              <IconButton size="small" onClick={() => setViewMode("grid")} color="primary">
                <ViewModuleIcon fontSize="small" />
              </IconButton>
            </GlassContainer>
          ) : (
            <IconButton
              size="small"
              onClick={() => setViewMode("grid")}
              color="default"
              sx={{ color: darkMode ? "rgba(249,250,251,0.65)" : "#111827" }}
            >
              <ViewModuleIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>

        {/* + New Meal Plan */}
        <GlassContainer
          blur={18}
          radius={999}
          sx={{ pl: 1.25, pr: 1.75, py: 0.4 }}
          dontHoverPop
        >
          <Button
            variant="text"
            startIcon={<AddIcon />}
            onClick={() => openWithMealPlan()}
            disabled={!trainerUserId}
            title={!trainerUserId ? "No trainer account loaded" : undefined}
            sx={{
              textTransform: "none",
              px: 0,
              minWidth: 0,
              color: baseTextColor,
            }}
          >
            New Meal Plan
          </Button>
        </GlassContainer>
      </Stack>

      {filteredPlans.length === 0 ? (
        <Typography variant="body2" sx={{ mt: 0, color: subtleTextColor }}>
          No meal plans found. Try creating one or adjusting your filters.
        </Typography>
      ) : viewMode === "list" ? (
        // LIST VIEW (table)
        <GlassContainer
          blur={18}
          radius={14}
          sx={{
            mt: 0,
            overflow: "hidden",
          }}
        >
          <TableContainer
            sx={{
              maxHeight: 600,
              backgroundColor: "transparent",
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow
                  sx={{
                    "& th": {
                      borderBottom: "1px solid",
                      borderBottomColor: darkMode
                        ? "rgba(255,255,255,0.12)"
                        : "rgba(0,0,0,0.06)",
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      backgroundColor: darkMode
                        ? "rgba(255,255,255,0.04)"
                        : "grey.50",
                      color: baseTextColor,
                      fontWeight: 500,
                    }}
                  >
                    <TableSortLabel
                      active={sortBy === "title"}
                      direction={sortBy === "title" ? sortDirection : "asc"}
                      onClick={() => handleSort("title")}
                      sx={{
                        "&.Mui-active": {
                          color: `${baseTextColor} !important`,
                        },
                        "& .MuiTableSortLabel-icon": {
                          color: `${baseTextColor} !important`,
                        },
                      }}
                    >
                      Title
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: darkMode
                        ? "rgba(255,255,255,0.04)"
                        : "grey.50",
                      color: baseTextColor,
                      fontWeight: 500,
                    }}
                  >
                    Recipes
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: darkMode
                        ? "rgba(255,255,255,0.04)"
                        : "grey.50",
                      color: baseTextColor,
                      fontWeight: 500,
                    }}
                  >
                    Tags
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      backgroundColor: darkMode
                        ? "rgba(255,255,255,0.04)"
                        : "grey.50",
                      color: baseTextColor,
                      fontWeight: 500,
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedForList.map((plan) => {
                  const summary = summarizeMealPlan(plan);

                  return (
                    <TableRow
                      key={plan.id}
                      hover
                      onClick={() => openWithMealPlan(plan)}
                      sx={{
                        cursor: "pointer",
                        "& .MuiTableCell-root": {
                          py: 1.25,
                          borderBottom: "1px solid",
                          borderBottomColor: darkMode
                            ? "rgba(255,255,255,0.12)"
                            : "rgba(0,0,0,0.06)",
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          color: baseTextColor,
                          fontWeight: 500,
                        }}
                      >
                        {plan.title || "Untitled Plan"}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: subtleTextColor,
                        }}
                      >
                        {summary.countRecipes} recipes
                      </TableCell>
                      <TableCell
                        sx={{
                          color: subtleTextColor,
                        }}
                      >
                        {plan.tags && plan.tags.length > 0 ? (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            {plan.tags.map((tag) => {
                              const colors = getTagColors(tag);
                              return (
                                <Chip
                                  key={tag}
                                  label={tag}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    borderRadius: 999,
                                    fontSize: "0.7rem",
                                    ...colors,
                                  }}
                                />
                              );
                            })}
                          </Stack>
                        ) : (
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              fontStyle: "italic",
                              color: subtleTextColor,
                            }}
                          >
                            No tags
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(ev) => handleRowDeleteClick(ev, plan.id)}
                          color="error"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </GlassContainer>
      ) : (
        // GRID / CARD VIEW (no create block here, only real plans)
        <Box
          sx={{
            display: "grid",
            gap: 1.25,
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            pb: 1,
          }}
        >
          {filteredPlans.map((plan) => {
            const summary = summarizeMealPlan(plan);
            const plannedUse =
              draft.id === plan.id
                ? draft.plannedUse
                : ((plan as any).plannedUse as SchedulePlan | undefined);

            return (
              <VaultGridCard
                key={plan.id}
                onClick={() => openWithMealPlan(plan)}
                minHeight={176}
                sx={{ color: baseTextColor }}
                title={plan.title || "Untitled Plan"}
                chips={
                  <>
                    <Chip
                      size="small"
                      icon={
                        <FitnessCenterIcon
                          sx={{
                            fontSize: "0.9rem !important",
                            color: baseTextColor,
                          }}
                        />
                      }
                      label={`${summary.countRecipes} recipes`}
                      variant="outlined"
                      sx={{
                        "& .MuiChip-label": {
                          fontWeight: 600,
                          fontSize: "0.7rem",
                          color: baseTextColor,
                        },
                        borderColor: darkMode
                          ? "rgba(255,255,255,0.4)"
                          : "rgba(0,0,0,0.2)",
                      }}
                    />
                    {plan.tags &&
                      plan.tags.length > 0 &&
                      plan.tags.map((tag) => {
                        const colors = getTagColors(tag);
                        return (
                          <Chip
                            key={tag}
                            size="small"
                            label={tag}
                            variant="outlined"
                            sx={{
                              "& .MuiChip-label": {
                                fontWeight: 500,
                                fontSize: "0.7rem",
                              },
                              borderRadius: 999,
                              ...colors,
                            }}
                          />
                        );
                      })}
                    {plannedUse && plannedUse.startDate && (
                      <Chip
                        size="small"
                        icon={
                          <CalendarMonthIcon
                            sx={{
                              fontSize: "0.9rem !important",
                              color: baseTextColor,
                            }}
                          />
                        }
                        label={formatPlannedUse(plannedUse)}
                        variant="outlined"
                        sx={{
                          "& .MuiChip-label": {
                            fontWeight: 600,
                            fontSize: "0.7rem",
                            color: baseTextColor,
                          },
                          borderColor: darkMode
                            ? "rgba(255,255,255,0.4)"
                            : "rgba(0,0,0,0.2)",
                        }}
                      />
                    )}
                  </>
                }
                body={
                  summary.topTitles.length > 0 ? (
                    <Typography
                      sx={{
                        mt: 0.25,
                        fontSize: "0.68rem",
                        lineHeight: 1.35,
                        color: subtleTextColor,
                      }}
                    >
                      {summary.topTitles.join(", ")}
                      {summary.countRecipes > summary.topTitles.length
                        ? `, +${summary.countRecipes - summary.topTitles.length} more`
                        : ""}
                    </Typography>
                  ) : (
                    <Typography
                      sx={{
                        mt: 0.25,
                        fontSize: "0.68rem",
                        lineHeight: 1.35,
                        fontStyle: "italic",
                        color: subtleTextColor,
                      }}
                    >
                      No recipes added yet
                    </Typography>
                  )
                }
                footer={
                  <Typography
                    sx={{
                      fontSize: "0.68rem",
                      lineHeight: 1.35,
                      fontStyle: "italic",
                      color: subtleTextColor,
                    }}
                  >
                    Tap to edit / assign
                  </Typography>
                }
              />
            );
          })}
        </Box>
      )}

      {/* Drawer editor */}
      <Drawer
        anchor="right"
        open={panelOpen}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 500, md: 600, lg: 680 },
            backgroundColor: drawerBg,
            color: drawerTextColor,
            borderLeft: drawerBorder,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* header row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 2,
            py: 1.5,
            borderBottom: drawerBorder,
            color: drawerTextColor,
          }}
        >
          <Typography
            sx={{
              flex: 1,
              fontWeight: 600,
              fontSize: "1rem",
              lineHeight: 1.2,
              pr: 1,
              color: drawerTextColor,
            }}
          >
            {mode === "CREATE"
              ? "New Meal Plan"
              : draft.title || "Edit Meal Plan"}
          </Typography>

          {mode === "EDIT" && (
            <IconButton
              size="small"
              onClick={handleDelete}
              sx={{ color: drawerTextColor, mr: 1 }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          )}

          <IconButton
            size="small"
            onClick={attemptCloseDrawer}
            sx={{ color: drawerTextColor }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* body content */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 2,
            py: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            color: drawerTextColor,
          }}
        >
          {/* DETAILS */}
          <Box>
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 0.4,
                mb: 1,
                color: drawerSubTextColor,
              }}
            >
              Details
            </Typography>
            <TextField
              label="Meal Plan Title"
              size="small"
              value={draft.title}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  title: e.target.value,
                }))
              }
              InputLabelProps={{
                sx: {
                  color: `${drawerTextColor} !important`,
                  fontSize: "0.75rem",
                  fontWeight: 500,
                },
              }}
              inputProps={{
                style: {
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  color: drawerTextColor,
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  color: drawerTextColor,
                  "& fieldset": {
                    borderColor: darkMode
                      ? "rgba(255,255,255,0.3)"
                      : "rgba(0,0,0,0.3)",
                  },
                  "&:hover fieldset": {
                    borderColor: darkMode
                      ? "rgba(255,255,255,0.6)"
                      : "rgba(0,0,0,0.6)",
                  },
                },
              }}
            />
          </Box>

          {/* TAGS EDITOR */}
          <Box>
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 0.4,
                mb: 1,
                color: drawerSubTextColor,
              }}
            >
              Tags
            </Typography>
            <Autocomplete
              multiple
              freeSolo
              size="small"
              options={allTags}
              value={draft.tags}
              onChange={(_e, value) =>
                setDraft((d) => ({
                  ...d,
                  tags: (value as string[])
                    .map((v) => v.trim())
                    .filter(Boolean),
                }))
              }
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const tagProps = getTagProps({ index });
                  const colors = getTagColors(option);
                  return (
                    <Chip
                      {...tagProps}
                      key={option + index}
                      label={option}
                      size="small"
                      sx={{
                        borderRadius: 999,
                        fontSize: "0.75rem",
                        ...colors,
                        "& .MuiChip-deleteIcon": {
                          color: colors.color,
                        },
                      }}
                    />
                  );
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Add / select tags"
                  placeholder="Type and press Enter to create, or pick existing"
                  InputLabelProps={{
                    sx: {
                      color: `${drawerTextColor} !important`,
                      fontSize: "0.75rem",
                      fontWeight: 500,
                    },
                  }}
                  inputProps={{
                    ...params.inputProps,
                    style: {
                      fontSize: "0.85rem",
                      color: drawerTextColor,
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      color: drawerTextColor,
                      "& fieldset": {
                        borderColor: darkMode
                          ? "rgba(255,255,255,0.3)"
                          : "rgba(0,0,0,0.3)",
                      },
                      "&:hover fieldset": {
                        borderColor: darkMode
                          ? "rgba(255,255,255,0.6)"
                          : "rgba(0,0,0,0.6)",
                      },
                    },
                  }}
                />
              )}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  color: drawerTextColor,
                  "& fieldset": {
                    borderColor: darkMode
                      ? "rgba(255,255,255,0.3)"
                      : "rgba(0,0,0,0.3)",
                  },
                  "&:hover fieldset": {
                    borderColor: darkMode
                      ? "rgba(255,255,255,0.6)"
                      : "rgba(0,0,0,0.6)",
                  },
                },
              }}
            />
          </Box>

          {/* RECIPES MULTISELECT */}
          <Box>
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 0.4,
                mb: 1,
                color: drawerSubTextColor,
              }}
            >
              Recipes in this plan
            </Typography>

            <Autocomplete
              multiple
              size="small"
              options={recipeOptions}
              disableCloseOnSelect
              getOptionLabel={(o) => o.label}
              value={selectedRecipeOptions}
              onChange={handleRecipeListChange}
              renderOption={(props, option, { selected }) => (
                <li
                  {...props}
                  key={option.recipeId}
                  style={{
                    color: drawerTextColor,
                    backgroundColor: darkMode ? "#1a1a1a" : "#fff",
                  }}
                >
                  <Checkbox
                    style={{ marginRight: 8 }}
                    checked={selected}
                    color={darkMode ? "secondary" : "primary"}
                  />
                  {option.label}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Add / remove recipes"
                  placeholder="Search recipes"
                  InputLabelProps={{
                    sx: {
                      color: `${drawerTextColor} !important`,
                      fontSize: "0.75rem",
                      fontWeight: 500,
                    },
                  }}
                  inputProps={{
                    ...params.inputProps,
                    style: {
                      fontSize: "0.85rem",
                      color: drawerTextColor,
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      color: drawerTextColor,
                      "& fieldset": {
                        borderColor: darkMode
                          ? "rgba(255,255,255,0.3)"
                          : "rgba(0,0,0,0.3)",
                      },
                      "&:hover fieldset": {
                        borderColor: darkMode
                          ? "rgba(255,255,255,0.6)"
                          : "rgba(0,0,0,0.6)",
                      },
                    },
                  }}
                />
              )}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  color: drawerTextColor,
                  "& fieldset": {
                    borderColor: darkMode
                      ? "rgba(255,255,255,0.3)"
                      : "rgba(0,0,0,0.3)",
                  },
                  "&:hover fieldset": {
                    borderColor: darkMode
                      ? "rgba(255,255,255,0.6)"
                      : "rgba(0,0,0,0.6)",
                  },
                },
                "& .MuiChip-root": {
                  borderRadius: 999,
                  color: drawerTextColor,
                  backgroundColor: darkMode
                    ? "rgba(255,255,255,0.16)"
                    : "rgba(0,0,0,0.04)",
                  borderColor: darkMode ? "rgba(255,255,255,0.4)" : undefined,
                  "& .MuiChip-label": {
                    color: drawerTextColor,
                  },
                  "& .MuiChip-icon": {
                    color: drawerTextColor,
                  },
                  "& .MuiChip-deleteIcon": {
                    color: drawerTextColor,
                  },
                },
              }}
            />

            {draft.recipes.length === 0 ? (
              <Typography
                sx={{
                  mt: 1,
                  fontSize: "0.8rem",
                  fontStyle: "italic",
                  color: drawerSubTextColor,
                }}
              >
                No recipes selected
              </Typography>
            ) : (
              <Stack spacing={1} sx={{ mt: 1 }}>
                {draft.recipes.map((ref, idx) => {
                  const found = recipes.find(
                    (r) => r.recipeId === ref.recipesId
                  );
                  return (
                    <Paper
                      key={ref.recipesId + idx}
                      elevation={0}
                      sx={{
                        p: 1.25,
                        borderRadius: 2,
                        border: drawerBorder,
                        backgroundColor: drawerFaintBg,
                        color: drawerTextColor,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.85rem",
                          mb: 0.25,
                          color: drawerTextColor,
                        }}
                      >
                        {found?.title || "Untitled Recipe"}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.7rem",
                          lineHeight: 1.3,
                          color: drawerSubTextColor,
                        }}
                      >
                        {found?.description || "No description available"}
                      </Typography>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Box>
        </Box>

        {/* footer actions */}
        <Box
          sx={{
            p: 2,
            borderTop: drawerBorder,
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            justifyContent: "flex-end",
            color: drawerTextColor,
          }}
        >
          <Button
            onClick={attemptCloseDrawer}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              fontWeight: 500,
              fontSize: "0.8rem",
              px: 1.5,
              py: 0.75,
              color: drawerTextColor,
              "&:hover": {
                backgroundColor: darkMode
                  ? "rgba(255,255,255,0.06)"
                  : "rgba(0,0,0,0.04)",
              },
            }}
          >
            Cancel
          </Button>

          <Button
            color="primary"
            variant="contained"
            onClick={handleSave}
            disabled={
              !draft.title?.trim() ||
              isFetchingToken ||
              !trainerUserId ||
              !idToken ||
              isSaving
            }
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              fontWeight: 600,
              fontSize: "0.8rem",
              px: 1.5,
              py: 0.75,
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {isSaving && (
              <CircularProgress size={16} thickness={4} color="inherit" />
            )}
            Save
          </Button>

          {mode === "EDIT" && (
            <Button
              color="error"
              onClick={handleDelete}
              disabled={
                isFetchingToken || !trainerUserId || !idToken || isSaving
              }
              sx={{
                textTransform: "none",
                borderRadius: "10px",
                fontWeight: 600,
                fontSize: "0.8rem",
                px: 1.5,
                py: 0.75,
                color: "#F44336",
                "&:hover": {
                  backgroundColor: darkMode
                    ? "rgba(255,255,255,0.06)"
                    : "rgba(0,0,0,0.04)",
                },
              }}
            >
              Delete
            </Button>
          )}
        </Box>
      </Drawer>

      {/* Discard confirmation dialog */}
      <Dialog
        open={confirmCloseOpen}
        onClose={() => setConfirmCloseOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: drawerBg,
            color: drawerTextColor,
            border: drawerBorder,
            borderRadius: 2,
            minWidth: 360,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem" }}>
          Discard changes?
        </DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              fontSize: "0.9rem",
              color: drawerSubTextColor,
              mt: 0.5,
            }}
          >
            If you close now, all unsaved changes to this meal plan will be
            lost.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button
            onClick={() => setConfirmCloseOpen(false)}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              fontWeight: 500,
              fontSize: "0.85rem",
              px: 1.5,
              py: 0.75,
              color: drawerTextColor,
              "&:hover": {
                backgroundColor: darkMode
                  ? "rgba(255,255,255,0.06)"
                  : "rgba(0,0,0,0.04)",
              },
            }}
          >
            Continue editing
          </Button>
          <Button
            onClick={actuallyCloseDrawer}
            color="error"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              fontWeight: 600,
              fontSize: "0.85rem",
              px: 1.5,
              py: 0.75,
            }}
          >
            Discard
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={
              !draft.title?.trim() ||
              isFetchingToken ||
              !trainerUserId ||
              !idToken ||
              isSaving
            }
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              fontWeight: 700,
              fontSize: "0.85rem",
              px: 1.5,
              py: 0.75,
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {isSaving && (
              <CircularProgress size={16} thickness={4} color="inherit" />
            )}
            Save changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
